import { cva, type VariantProps } from "class-variance-authority";
import {
  Button as AriaButton,
  composeRenderProps,
  type ButtonProps as AriaButtonProps,
} from "react-aria-components";
import type { PropsWithChildren } from "react";
import { forwardRef } from "react";
import { tw } from "src/design-system/tw";

const iconButtonVariants = cva(
  [
    "inline-flex cursor-pointer items-center justify-center rounded-md",
    "transition-colors duration-150",
    "data-disabled:cursor-default data-disabled:opacity-50",
    "data-focus-visible:z-10 data-focus-visible:outline-2 data-focus-visible:outline-offset-2",
  ],
  {
    variants: {
      variant: {
        primary: [
          "bg-fill-brand text-text-on-bg-fill",
          "data-hovered:bg-fill-brand-hover",
          "data-pressed:bg-fill-brand-active",
        ],
        secondary: [
          "bg-fill-primary text-text-primary",
          "data-hovered:bg-fill-primary-hover",
          "data-pressed:bg-fill-primary-active",
        ],
        subtle: [
          "bg-transparent text-text-primary",
          "data-hovered:bg-fill-secondary-hover",
          "data-pressed:bg-fill-secondary-active",
        ],
        elevated: [
          "bg-fill-solid text-text-primary shadow-sm",
          "data-hovered:bg-fill-solid-hover",
          "data-pressed:bg-fill-solid-active",
        ],
      },
      size: {
        sm: "p-1 text-label-md",
        md: "p-2 text-label-lg",
        lg: "p-3 text-label-xl",
      },
    },
    defaultVariants: {
      variant: "primary",
      size: "md",
    },
  },
);

type IconButtonProps = AriaButtonProps &
  VariantProps<typeof iconButtonVariants> &
  PropsWithChildren;

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(
  ({ className, variant, size, children, ...props }, ref) => {
    return (
      <AriaButton
        ref={ref}
        className={composeRenderProps(className, (className) =>
          tw(
            iconButtonVariants({
              variant,
              size,
              className,
            }),
          ),
        )}
        {...props}
      >
        {children}
      </AriaButton>
    );
  },
);

IconButton.displayName = "IconButton";
